import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import { loadVaultList, type VaultFlowDeps, type VaultListResult } from '../services/vaultFlow'
import { isWeakPassword } from '../services/reportAnalysis'
import { type VaultItem } from '../types'

/**
 * ホーム画面のデータ取得 state machine (T-D-Home)。
 *
 * 設計判断:
 *   - `loadVaultList` を 1 度だけ呼び、結果から `count / recent / favorites` を導出。
 *     loadVaultList は updatedAt 降順ソート済みのため、top-N スライスで効率的に取得可能。
 *   - `reportsStats / sendCount / activity` は Phase 3 未実装のため null 固定。
 *     Reports / Send 実装時はここに追加するだけで HomePage 側は受け取るだけで完結する。
 *   - VaultFlowDeps DI でテスト可能、network なし branch カバレッジを取得。
 */

export interface HomeSecurityStats {
  /** 弱いパスワード件数 (長さ 10 未満を heuristic で判定) */
  readonly weakCount: number
  /** 使い回しパスワード件数 (同一 password を持つ login item 数) */
  readonly reusedCount: number
  /** 0-100 のセキュリティスコア (簡易計算、weak / reused の比率から導出) */
  readonly score: number
  /** TOTP secret を持つ login item (TOTP quick access widget 用、上限 6 件) */
  readonly totpItems: readonly VaultItem[]
}

export type HomeDataState =
  | { kind: 'loading' }
  | {
      kind: 'ready'
      count: number
      recent: readonly VaultItem[]
      favorites: readonly VaultItem[]
      failureCount: number
      /** F12: client-side で計算した weak / reused / score + totpItems */
      security: HomeSecurityStats
      /** Phase 3 Later — Reports 実装時に null から差し替え */
      reportsStats: null
      /** Phase 3 Later — Send 実装時に null から差し替え */
      sendCount: null
      /** Phase 3 Later — Activity API 実装時に null から差し替え */
      activity: null
    }
  | { kind: 'error'; messageKey: string }

const RECENT_LIMIT = 5
const FAV_LIMIT = 6
const TOTP_LIMIT = 6

/** 簡易 security score 計算 (0-100): item が多いほど weak/reused ペナルティが響かない設計。 */
const computeSecurityScore = (total: number, weak: number, reused: number): number => {
  if (total === 0) return 100
  const weakRatio = weak / total
  const reusedRatio = reused / total
  // 100 から weakRatio で最大 50 / reusedRatio で最大 50 を減点
  const penalty = Math.round(weakRatio * 50 + reusedRatio * 50)

  return Math.max(0, Math.min(100, 100 - penalty))
}

const computeSecurityStats = (items: readonly VaultItem[]): HomeSecurityStats => {
  const passwordCounts = new Map<string, number>()
  let weakCount = 0
  const totpItems: VaultItem[] = []

  for (const it of items) {
    if (it.payload.type !== 'login') continue
    const pw = it.payload.password
    if (pw && pw.length > 0) {
      passwordCounts.set(pw, (passwordCounts.get(pw) ?? 0) + 1)
      if (isWeakPassword(pw)) weakCount += 1
    }
    if (it.payload.totpSecret && totpItems.length < TOTP_LIMIT) {
      totpItems.push(it)
    }
  }

  // 使い回し = 重複する password を持つ login の総数
  let reusedCount = 0
  for (const count of passwordCounts.values()) {
    if (count > 1) reusedCount += count
  }

  const loginCount = items.filter(it => it.payload.type === 'login').length
  const score = computeSecurityScore(loginCount, weakCount, reusedCount)

  return { weakCount, reusedCount, score, totpItems }
}

const ERROR_KEYS = {
  no_session: 'home.error.no_session',
  network: 'home.error.network',
  generic: 'home.error.generic',
} as const

const messageKeyFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return ERROR_KEYS.no_session

    return ERROR_KEYS.network
  }

  return ERROR_KEYS.generic
}

export interface UseHomeDataOptions {
  flowDeps?: VaultFlowDeps
  /**
   * テスト用 DI: loadVaultList 全体を差し替える。
   * 指定した場合 `flowDeps` より優先する。spec で Web Crypto をバイパスするために使用。
   */
  loadFn?: (sk: Uint8Array) => Promise<VaultListResult>
}

export const useHomeData = (options: UseHomeDataOptions = {}): HomeDataState => {
  const { loadFn, flowDeps } = options
  const [state, setState] = useState<HomeDataState>({ kind: 'loading' })
  const mountedRef = useRef(true)

  const fetch = useCallback(async (): Promise<void> => {
    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      if (mountedRef.current) {
        setState({ kind: 'error', messageKey: ERROR_KEYS.no_session })
      }

      return
    }

    try {
      const result = loadFn
        ? await loadFn(session.symmetricKey)
        : await loadVaultList(session.symmetricKey, flowDeps)

      if (!mountedRef.current) return

      const allItems = result.items
      const recent = allItems.slice(0, RECENT_LIMIT)
      const favorites = allItems.filter(it => it.payload.favorite === true).slice(0, FAV_LIMIT)

      setState({
        kind: 'ready',
        count: allItems.length,
        recent,
        favorites,
        failureCount: result.failures.length,
        security: computeSecurityStats(allItems),
        reportsStats: null,
        sendCount: null,
        activity: null,
      })
    } catch (err) {
      if (!mountedRef.current) return
      setState({ kind: 'error', messageKey: messageKeyFor(err) })
    }
  }, [loadFn, flowDeps])

  useEffect(() => {
    mountedRef.current = true
    void fetch()

    return () => {
      mountedRef.current = false
    }
  }, [fetch])

  return useMemo(() => state, [state])
}
